'use client'

import { Pip } from '@/design-system'

const MONO: React.CSSProperties = { fontFamily: 'var(--font-mono)', fontWeight: 500 }

const VIDEO_COMMENTS = [
  { at: 0.22, t: '0:08', text: 'Cut on the action' },
  { at: 0.58, t: '0:24', text: 'Color is too warm' },
  { at: 0.86, t: '0:38', text: 'Final approved', approved: true }
] as const

const VIDEO_FRAME_COLORS = ['var(--sky)', 'var(--mauve)', 'var(--yellow)', 'var(--acid)'] as const

export function VideoBody({ phase }: { phase: number }) {
  const playhead = phase
  const visibleComment = [...VIDEO_COMMENTS].reverse().find((c) => phase > c.at)
  const frameIdx = Math.min(VIDEO_FRAME_COLORS.length - 1, Math.floor(playhead * VIDEO_FRAME_COLORS.length))
  const totalSec = 44
  const cur = Math.floor(playhead * totalSec)
  const curStr = `${Math.floor(cur / 60)}:${(cur % 60).toString().padStart(2, '0')}`
  const noteCount = VIDEO_COMMENTS.filter((c) => phase > c.at).length

  return (
    <div style={{ padding: 18, position: 'relative', minHeight: 320 }}>
      <div
        style={{
          border: '2.5px solid var(--ink)',
          position: 'relative',
          aspectRatio: '16/9',
          background: VIDEO_FRAME_COLORS[frameIdx],
          marginBottom: 12,
          overflow: 'hidden',
          boxShadow: 'var(--shadow-sm)',
          transition: 'background .4s'
        }}>
        <svg
          width="100%"
          height="100%"
          style={{ display: 'block' }}
          preserveAspectRatio="none"
          viewBox="0 0 160 90"
          aria-hidden>
          <defs>
            <pattern id="proof-vp" width={8} height={8} patternUnits="userSpaceOnUse">
              <circle cx={4} cy={4} r={1} fill="rgba(10,10,10,0.18)" />
            </pattern>
          </defs>
          <rect width={160} height={90} fill="url(#proof-vp)" />
          <rect x={60} y={30} width={40} height={50} fill="rgba(10,10,10,0.28)" />
          <circle cx={80} cy={22} r={9} fill="rgba(10,10,10,0.32)" />
        </svg>

        <div
          style={{
            position: 'absolute',
            top: 8,
            left: 8,
            background: 'var(--ink)',
            color: 'var(--paper)',
            padding: '3px 7px',
            fontFamily: 'var(--font-mono)',
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: '0.08em'
          }}>
          {curStr} / 0:44
        </div>

        {visibleComment && (
          <div
            key={visibleComment.t}
            style={{
              position: 'absolute',
              bottom: 10,
              left: 10,
              right: 10,
              border: '2px solid var(--ink)',
              background: 'approved' in visibleComment ? 'var(--acid)' : 'var(--yellow)',
              padding: '6px 10px',
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              boxShadow: 'var(--shadow-sm)'
            }}>
            <span
              style={{
                ...MONO,
                fontSize: 9,
                fontWeight: 700,
                letterSpacing: '0.1em',
                textTransform: 'uppercase',
                background: 'var(--ink)',
                color: 'var(--paper)',
                padding: '2px 5px'
              }}>
              @{visibleComment.t}
            </span>
            <span style={{ ...MONO, fontSize: 11, flex: 1, opacity: 0.9 }}>&quot;{visibleComment.text}&quot;</span>
          </div>
        )}

        <div
          aria-hidden
          style={{
            position: 'absolute',
            inset: 0,
            display: 'grid',
            placeItems: 'center',
            pointerEvents: 'none'
          }}>
          <div
            style={{
              width: 48,
              height: 48,
              background: 'var(--paper)',
              border: '2.5px solid var(--ink)',
              display: 'grid',
              placeItems: 'center',
              boxShadow: 'var(--shadow-sm)',
              opacity: 0.85
            }}>
            <svg width={20} height={20} viewBox="0 0 24 24" aria-hidden>
              <path d="M7 5 L7 19 L19 12 Z" fill="var(--ink)" />
            </svg>
          </div>
        </div>
      </div>

      <div style={{ position: 'relative', height: 18, marginBottom: 14 }}>
        <div
          style={{
            position: 'absolute',
            top: 6,
            left: 0,
            right: 0,
            height: 6,
            border: '2px solid var(--ink)',
            background: 'var(--paper)'
          }}>
          <div style={{ position: 'absolute', inset: 0, width: `${playhead * 100}%`, background: 'var(--ink)' }} />
        </div>
        {VIDEO_COMMENTS.map((c, i) => (
          <div
            key={i}
            aria-hidden
            style={{
              position: 'absolute',
              top: 0,
              left: `${c.at * 100}%`,
              transform: 'translateX(-50%)',
              width: 12,
              height: 18,
              background: 'var(--yellow)',
              border: '2px solid var(--ink)',
              opacity: phase > c.at - 0.02 ? 1 : 0.3,
              transition: 'opacity .2s'
            }}
          />
        ))}
        <div
          aria-hidden
          style={{
            position: 'absolute',
            top: -2,
            left: `calc(${playhead * 100}% - 2px)`,
            width: 4,
            height: 14,
            background: 'var(--hot)',
            boxShadow: '0 0 0 1.5px var(--ink)'
          }}
        />
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '36px 1fr auto',
          gap: 10,
          alignItems: 'center',
          padding: '8px 10px',
          border: '2px solid var(--ink)',
          background: 'var(--paper)'
        }}>
        <span
          style={{
            width: 28,
            height: 28,
            background: 'var(--hot)',
            border: '2px solid var(--ink)',
            display: 'grid',
            placeItems: 'center'
          }}>
          <svg width={14} height={14} viewBox="0 0 24 24" fill="var(--paper)" aria-hidden>
            <path d="M8 5v14l11-7z" />
          </svg>
        </span>
        <div>
          <div style={{ ...MONO, fontSize: 11, fontWeight: 700 }}>hero-cut-v3.mov</div>
          <div style={{ ...MONO, fontSize: 10, opacity: 0.6 }}>MOV · 820 MB · 4K</div>
        </div>
        <Pip bg="var(--acid)" style={{ fontSize: 9, padding: '2px 6px' }}>
          {noteCount} NOTES
        </Pip>
      </div>
    </div>
  )
}
